import React, { useCallback, useEffect, useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import FileSaver from 'file-saver';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import type { IResourceDatabaseView } from '@/api/types';
import type { IDatabaseViewColumnNames } from '@/components/common';

import { selectFoldersList, selectPendingAction } from '@/store/selectors/resourcesSelectors';
import { useAppSelector, useTableSort } from '@/hooks';
import { databaseViewSortingFunctions } from '@/helpers';
import { ResourceDatabaseViewsTable } from './ResourceDatabaseViewsTable';
import { getDatabaseViewsList } from '@/store/slices/resourcesSlice';
import { apiResources } from '@/api/resources';
import { UserType } from '@/store/enums';
import { DATE_ISO_FORMAT } from '@/config/variables';
import { LoaderWrapper, TabsResourcesNoData, ResourceDatabaseTableSkeleton } from '@/components/common';
import routes from '@/config/routes';

const resourceDatabaseViewsContainerSelector = createSelector(
  selectFoldersList,
  selectPendingAction(getDatabaseViewsList.typePrefix),
  (foldersList, isViewsListPending) => ({
    foldersList,
    isPending: isViewsListPending,
  }),
);

interface IProps {
  items: IResourceDatabaseView[];
  databaseId: number;
  clientId: number;
  className?: string;
}

const ResourceDatabaseViewsContainer = ({ items, databaseId, clientId, className }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { foldersList, isPending } = useAppSelector(resourceDatabaseViewsContainerSelector);
  const { sortedData, onSort, setRawData, sortingState } = useTableSort<
    IDatabaseViewColumnNames,
    IResourceDatabaseView
  >({
    initial: { name: 'DESC' },
    sortingFunctions: databaseViewSortingFunctions,
  });
  const history = useHistory();

  const database = useMemo(
    () =>
      foldersList
        ?.find(clientFolders => clientFolders.clientId === clientId)
        ?.database.find(database => database.id === databaseId),
    [foldersList, clientId, databaseId],
  );

  useEffect(() => {
    const viewsWithDefault = items.map(view => ({ ...view, isDefault: view.id === database?.default_view_id }));
    setRawData(viewsWithDefault);
  }, [database?.default_view_id, items, setRawData]);

  const selectViewAction = useCallback(
    (viewId: number) => {
      history.push(generatePath(routes.sp.scheduling.resources.view, { client: clientId, dbId: databaseId, viewId }));
    },
    [clientId, databaseId, history],
  );

  const exportViewAction = useCallback(
    async (viewId: number, viewName: string) => {
      const { data } = await apiResources.getDatabaseViewExport({
        dbId: databaseId,
        viewId: viewId,
        userType: UserType.client,
      });

      if (data) {
        const file = new Blob([data], { type: 'text/csv;charset=utf-8' });
        FileSaver.saveAs(file, `${viewName}-${format(new Date(), DATE_ISO_FORMAT)}.csv`);
      }
    },
    [databaseId],
  );

  return (
    <LoaderWrapper isLoading={isPending} loader={<ResourceDatabaseTableSkeleton />}>
      {sortedData.length ? (
        <ResourceDatabaseViewsTable
          items={sortedData}
          sortingState={sortingState}
          onSort={onSort}
          className={className}
          selectViewAction={selectViewAction}
          exportViewAction={exportViewAction}
        />
      ) : (
        <TabsResourcesNoData label={t`resources.databases.noViews`} first />
      )}
    </LoaderWrapper>
  );
};

export { ResourceDatabaseViewsContainer };
